import React from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Function to aggregate data when there are too many points
const aggregateData = (data, maxPoints = 30) => {
  if (data.length <= maxPoints) {
    return data;
  }

  const chunkSize = Math.ceil(data.length / maxPoints);
  const aggregated = [];

  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    const avgPlayCount = Math.round(
      chunk.reduce((sum, item) => sum + item.playCount, 0) / chunk.length
    );
    const avgUniquePlayers = Math.round(
      chunk.reduce((sum, item) => sum + item.uniquePlayers, 0) / chunk.length
    );

    // Use the middle date of the chunk for the label
    const middleIndex = Math.floor(chunk.length / 2);
    aggregated.push({
      name: chunk[middleIndex].name,
      playCount: avgPlayCount,
      uniquePlayers: avgUniquePlayers,
      label: chunk[middleIndex].label,
    });
  }

  return aggregated;
};

// Function to format Roulette Trends data for chart
const formatRouletteTrendsDataForChart = (data, intervalLabel) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const formatted = data
    .map((item) => {
      const dateStr = item[0];
      let displayName;

      if (typeof dateStr === "string") {
        if (dateStr.includes(" ")) {
          const date = new Date(dateStr);
          if (intervalLabel.includes("Minute")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } else if (intervalLabel.includes("Hour")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              hour12: true,
            });
          } else {
            displayName = date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          }
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(dateStr);
          displayName = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } else {
          displayName = dateStr;
        }
      } else {
        displayName = String(dateStr);
      }

      return {
        name: displayName,
        playCount: item[1],
        uniquePlayers: item[2],
        label: displayName,
      };
    })
    .reverse();

  // Aggregate if too many data points
  return aggregateData(formatted, 40);
};

// Custom Tooltip for Play Count Chart
const PlayCountTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "12px",
          fontSize: "12px",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#2d3748" }}>
          {label}
        </p>
        <p style={{ margin: "4px 0", color: "#8b5cf6" }}>
          <strong>Roulette Plays:</strong>{" "}
          {data.playCount?.toLocaleString() || 0}
        </p>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for Unique Players Chart
const UniquePlayersTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "12px",
          fontSize: "12px",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#2d3748" }}>
          {label}
        </p>
        <p style={{ margin: "4px 0", color: "#ec4899" }}>
          <strong>Unique Players:</strong>{" "}
          {data.uniquePlayers?.toLocaleString() || 0}
        </p>
      </div>
    );
  }
  return null;
};

// Component to render Roulette Play Count Chart
export const RoulettePlayCountChart = ({ data, interval }) => {
  const chartData = formatRouletteTrendsDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          color: "#a0aec0",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        📊 No data available
        <small style={{ fontSize: "11px", color: "#cbd5e0" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <defs>
          <linearGradient id="colorPlayCount" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "11px" }}
          interval={Math.floor(chartData.length / 8)}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
        />
        <Tooltip content={<PlayCountTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px" }}
          formatter={() => "Roulette Plays"}
        />
        <Area
          type="monotone"
          dataKey="playCount"
          stroke="#8b5cf6"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorPlayCount)"
          name="Roulette Plays"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Component to render Unique Players Chart
export const RouletteUniquePlayersChart = ({ data, interval }) => {
  const chartData = formatRouletteTrendsDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          color: "#a0aec0",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        📊 No data available
        <small style={{ fontSize: "11px", color: "#cbd5e0" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "11px" }}
          interval={Math.floor(chartData.length / 8)}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
        />
        <Tooltip content={<UniquePlayersTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px" }}
          formatter={() => "Unique Players"}
        />
        <Line
          type="monotone"
          dataKey="uniquePlayers"
          stroke="#ec4899"
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 6 }}
          name="Unique Players"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Default export for backward compatibility
export default RoulettePlayCountChart;
